import React, { useCallback, useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { Button } from "react-bootstrap";
import EnrollmentService from "../../_services/EnrollmentService";
import { openStudentGradeReport } from "../../_services/reports.service";
import { useAccessControl } from "../../access-control/AccessControlContext";
import EnrollmentSidebar from "./components/EnrollmentSidebar";
import ModulesSection from "./components/ModulesSection";
import DocumentsSection from "./components/DocumentsSection";
import PlanDetailsSection from "./components/PlanDetailsSection";
import DiscountLogSection from "./components/DiscountLogSection";
import PaymentDetailsSection from "./components/PaymentDetailsSection";
import StatusHistorySection from "./components/StatusHistorySection";
import CreditNotesSection from "./components/CreditNotesSection";
import RefundSection from "./components/RefundSection";
import ExcessPaymentsSection from "./components/ExcessPaymentsSection";
import PaymentFollowupSection from "./components/PaymentFollowupSection";
import CollectPaymentModal from "./components/CollectPaymentModal";
import ApplyDiscountModal from "./components/ApplyDiscountModal";
import GenerateInvoiceModal from "./components/GenerateInvoiceModal";
import EnrollmentFormModal from "./components/EnrollmentFormModal";
import ReconcileModal from "./components/ReconcileModal";
import "./enrollment-view.css";

const EnrollmentViewPage: React.FC = () => {
  const { enrollmentId } = useParams<{ enrollmentId: string }>();
  const eid = Number(enrollmentId);

  const [details, setDetails] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Plan & payments shared state (loaded once, passed to 3 sections)
  const [planData, setPlanData] = useState<any>(null);
  const [planLoading, setPlanLoading] = useState(true);

  // Modal states
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [showDiscountModal, setShowDiscountModal] = useState(false);
  const [showInvoiceModal, setShowInvoiceModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showReconcileModal, setShowReconcileModal] = useState(false);

  // Grade report state
  const [gradeReportLoading, setGradeReportLoading] = useState(false);
  const [gradeReportError, setGradeReportError] = useState("");

  // Toolbar authorization (role-based, mirrors Java button permissions).
  // Permissive by default — buttons only hide once an admin restricts them.
  const { canSeeElement } = useAccessControl();
  const PK = "enrollment-view";
  const canCollectPayment = canSeeElement(PK, "action.collectPayment");
  const canApplyDiscount = canSeeElement(PK, "action.applyDiscount");
  const canGenerateInvoice = canSeeElement(PK, "action.generateInvoice");
  const canStatementOfAccount = canSeeElement(PK, "action.statementOfAccount");
  const canReconcile = canSeeElement(PK, "action.reconcile");
  const canEdit = canSeeElement(PK, "action.edit");
  const canGradeReport = canSeeElement(PK, "action.gradeReport");

  const loadPlanAndPayments = useCallback(() => {
    if (!eid) return;
    setPlanLoading(true);
    EnrollmentService.getPlanAndPayments(eid)
      .then((res: any) => {
        if (res.data?.success) setPlanData(res.data.data);
      })
      .catch(console.error)
      .finally(() => setPlanLoading(false));
  }, [eid]);

  useEffect(() => {
    if (!eid) return;

    // Load enrollment details
    EnrollmentService.getDetails(eid)
      .then((res: any) => {
        if (res.data?.success) {
          setDetails(res.data.data);
        } else {
          setError(res.data?.message || "Failed to load enrollment");
        }
      })
      .catch((err: any) => setError(err.message || "Failed to load enrollment"))
      .finally(() => setLoading(false));

    // Load plan & payments
    loadPlanAndPayments();
  }, [eid, loadPlanAndPayments]);

  if (!eid) {
    return (
      <div className="enrollment-view-page">
        <div style={{ textAlign: "center", padding: 40, color: "#d32f2f" }}>
          Invalid enrollment ID
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="enrollment-view-page">
        <div className="ev-loading" style={{ padding: 60 }}>
          <i className="fa fa-spinner fa-spin" style={{ fontSize: 20 }} />
          <span style={{ marginLeft: 10 }}>Loading enrollment details...</span>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="enrollment-view-page">
        <div style={{ textAlign: "center", padding: 40, color: "#d32f2f" }}>
          {error}
        </div>
      </div>
    );
  }

  // Button visibility driven by resource_master (id 202) jsonConfig, like Java's
  // permissionsJsonBased (show + role + enrollment-status gated). Falls back to
  // the access-control ACL for tenants without the config.
  const perms = details?.permissions;
  const hasJsonPerms = !!perms && Object.keys(perms.buttons || {}).length > 0;
  const btnPerm = (key: string, fallback: boolean) =>
    hasJsonPerms ? !!perms?.buttons?.[key] : fallback;
  const showApplyDiscount = btnPerm("addDiscount", canApplyDiscount);
  const showStatementOfAccount = btnPerm("statementOfAccount", canStatementOfAccount);
  const showReconcile = btnPerm("changePlan", canReconcile);
  const showEdit = hasJsonPerms ? !!perms?.canEdit : canEdit;
  // Per-row action perms (undefined → the section falls back to its own ACL).
  const printReceiptPerm = hasJsonPerms ? !!perms?.buttons?.printReceipt : undefined;
  const reversePaymentPerm = hasJsonPerms ? !!perms?.buttons?.reversePayment : undefined;
  const reverseDiscountPerm = hasJsonPerms ? !!perms?.buttons?.reverseDiscount : undefined;

  return (
    <div className="enrollment-view-page">
      {/* Banner for last status update */}
      {details?.lastStatusUpdate && (
        <div className="ev-banner">{details.lastStatusUpdate}</div>
      )}

      {/* Action Bar */}
      <div className="ev-action-bar">
        {canCollectPayment && (
        <Button
          variant="primary"
          size="sm"
          onClick={() => {
            const params = new URLSearchParams();
            if (details?.studentCode) params.set("studentCode", details.studentCode);
            params.set("enrollmentId", String(eid));
            window.open(`/payment-console?${params.toString()}`, "_blank");
          }}
        >
          <i className="fa fa-money" /> Collect Payment
        </Button>
        )}
        {showApplyDiscount && (
        <Button
          variant="secondary"
          size="sm"
          onClick={() => setShowDiscountModal(true)}
          style={{ marginLeft: 8 }}
        >
          <i className="fa fa-percent" /> Apply Discount
        </Button>
        )}
        {/* Invoicing only applies to invoice-based tenants */}
        {canGenerateInvoice && planData?.invoiceBasedSystem && (
        <Button
          variant="secondary"
          size="sm"
          onClick={() => setShowInvoiceModal(true)}
          style={{ marginLeft: 8 }}
        >
          <i className="fa fa-file-text" /> Generate Invoice
        </Button>
        )}
        {showStatementOfAccount && (
        <Button
          variant="secondary"
          size="sm"
          onClick={async () => {
            const printWin = window.open("", "_blank");
            if (!printWin) return;
            printWin.document.write("<html><body><p>Loading Statement of Account...</p></body></html>");
            try {
              const res: any = await EnrollmentService.getSOA(eid);
              if (res.data?.success && res.data.html) {
                printWin.document.open();
                printWin.document.write(res.data.html);
                printWin.document.close();
                printWin.focus();
                printWin.print();
              } else {
                printWin.document.open();
                printWin.document.write("<html><body><p>Failed to load Statement of Account.</p></body></html>");
                printWin.document.close();
              }
            } catch (err) {
              console.error("Error loading SOA:", err);
              printWin.document.open();
              printWin.document.write("<html><body><p>Error loading Statement of Account.</p></body></html>");
              printWin.document.close();
            }
          }}
          style={{ marginLeft: 8 }}
        >
          <i className="fa fa-print" /> Statement of Account
        </Button>
        )}
        {showReconcile && (
        <Button
          variant="secondary"
          size="sm"
          onClick={() => setShowReconcileModal(true)}
          style={{ marginLeft: 8 }}
        >
          <i className="fa fa-refresh" /> Reconcile
        </Button>
        )}
        {showEdit && (
        <Button
          variant="secondary"
          size="sm"
          onClick={() => setShowEditModal(true)}
          style={{ marginLeft: 8 }}
        >
          <i className="fa fa-pencil" /> Edit
        </Button>
        )}
        {canGradeReport && details?.enquiryId && details?.batchId && (
          <Button
            variant="secondary"
            size="sm"
            disabled={gradeReportLoading}
            onClick={async () => {
              setGradeReportLoading(true);
              setGradeReportError("");
              try {
                const result = await openStudentGradeReport(details.enquiryId, details.batchId);
                if (!result.success) setGradeReportError(result.message);
              } catch (err: any) {
                setGradeReportError(err.message || "Failed to generate grade report");
              } finally {
                setGradeReportLoading(false);
              }
            }}
            style={{ marginLeft: 8 }}
          >
            {gradeReportLoading ? (
              <><i className="fa fa-spinner fa-spin" /> Generating...</>
            ) : (
              <><i className="fa fa-file-text-o" /> Grade Report</>
            )}
          </Button>
        )}
      </div>
      {gradeReportError && (
        <div style={{ padding: "6px 12px", background: "var(--status-danger-bg)", color: "var(--status-danger)", borderRadius: 4, fontSize: 12, marginBottom: 6 }}>
          {gradeReportError}
        </div>
      )}

      <div className="ev-layout">
        {/* Main Content */}
        <div className="ev-main">
          <ModulesSection enrollmentId={eid} />
          <DocumentsSection enrollmentId={eid} />
          <PlanDetailsSection planData={planData} loading={planLoading} />
          <DiscountLogSection
            transactions={planData?.paymentTransactions || []}
            loading={planLoading}
            enrollmentId={eid}
            onRefresh={loadPlanAndPayments}
            reverseDiscountPerm={reverseDiscountPerm}
          />
          <PaymentDetailsSection
            transactions={planData?.paymentTransactions || []}
            loading={planLoading}
            enrollmentId={eid}
            onRefresh={loadPlanAndPayments}
            printReceiptPerm={printReceiptPerm}
            reversePaymentPerm={reversePaymentPerm}
          />
          <ExcessPaymentsSection enrollmentId={eid} />
          <PaymentFollowupSection enrollmentId={eid} />
          <CreditNotesSection enrollmentId={eid} />
          <RefundSection enrollmentId={eid} />
          <StatusHistorySection enrollmentId={eid} />
        </div>

        {/* Sidebar */}
        <EnrollmentSidebar details={details} />
      </div>

      {/* Payment Modal */}
      <CollectPaymentModal
        show={showPaymentModal}
        onHide={() => setShowPaymentModal(false)}
        enrollmentId={eid}
        onSuccess={loadPlanAndPayments}
        schools={details?.schoolId ? [{ id: details.schoolId, name: details.schoolName || "" }] : []}
        defaultSchoolId={details?.schoolId || null}
      />

      {/* Discount Modal */}
      <ApplyDiscountModal
        show={showDiscountModal}
        onHide={() => setShowDiscountModal(false)}
        enrollmentId={eid}
        onSuccess={loadPlanAndPayments}
      />

      {/* Generate Invoice Modal */}
      <GenerateInvoiceModal
        show={showInvoiceModal}
        onHide={() => setShowInvoiceModal(false)}
        enrollmentId={eid}
        onSuccess={loadPlanAndPayments}
      />

      {/* Reconcile Modal */}
      {showReconcileModal && (
        <ReconcileModal
          show={showReconcileModal}
          onHide={() => setShowReconcileModal(false)}
          enrollmentId={eid}
          details={details}
          invoiceGenerated={(planData?.invoices?.length || 0) > 0}
          onSuccess={() => {
            setShowReconcileModal(false);
            loadPlanAndPayments();
          }}
        />
      )}

      {/* Edit Enrollment Modal */}
      <EnrollmentFormModal
        show={showEditModal}
        onHide={() => setShowEditModal(false)}
        mode="edit"
        enrollmentId={eid}
        existingData={
          details
            ? {
                batchId: details.batchId,
                statusId: details.statusId,
                joinDateTime: details.joiningDateRaw,
                studentCode: details.studentCode,
                studentCode2: details.studentCode2,
                installmentTemplateId: details.installmentTemplateId,
                remarks: details.remarks,
                lateralEntry: details.lateralEntry,
                graduationDate: details.graduationDateRaw,
                gradePoint: details.gradePoint,
                studentEnrollmentCategoryId:
                  details.studentEnrollmentCategoryId,
                studentEnrollmentCategory2Id:
                  details.studentEnrollmentCategory2Id,
                schoolId: details.schoolId,
                yearId: details.batchYearId,
                entryYearId: details.entryYearId,
                currentYearId: details.currentYearId,
                programId: details.programId,
                courseTypeName: details.courseTypeName,
              }
            : undefined
        }
        onSuccess={() => {
          setShowEditModal(false);
          setLoading(true);
          EnrollmentService.getDetails(eid)
            .then((res: any) => {
              if (res.data?.success) setDetails(res.data.data);
            })
            .catch(console.error)
            .finally(() => setLoading(false));
        }}
      />
    </div>
  );
};

export default EnrollmentViewPage;
